import { FitAnswer, FitAssessment } from "./api";

export interface MissQuestion {
	item: string;
	category: keyof FitAssessment;
	question: string;
}

export function missQuestions(assessment: FitAssessment): MissQuestion[] {
	const questions: MissQuestion[] = [];
	(Object.keys(assessment) as (keyof FitAssessment)[]).forEach((category) => {
		assessment[category].items.forEach((item) => {
			if (item.status === "Miss" && item.necessity === "required") {
				questions.push({
					item: item.item,
					category,
					question: `Your CV doesn't show "${item.item}". Do you have it?`,
				});
			}
		});
	});
	return questions;
}

export function requiredStats(assessment: FitAssessment): { met: number; total: number } {
	const categories = [assessment.experience, assessment.skills, assessment.education, assessment.certifications];
	let total = 0;
	let met = 0;
	for (const category of categories) {
		for (const item of category.items) {
			if (item.necessity !== "required") continue;
			total += 1;
			if (item.status === "Fit") met += 1;
		}
	}
	return { met, total };
}

// "review" while gaps are still unanswered — we cannot judge fit until the user sorts them. Once
// every gap is answered, strength comes from what is genuinely covered (on the CV plus confirmed
// "have"); confirmed "lack" is the only thing that truly pulls fit down.
export type FitVerdict = "strong" | "partial" | "weak" | "review";

const STRONG_THRESHOLD = 0.7;
const PARTIAL_THRESHOLD = 0.4;

export interface ResolvedFit {
	total: number;
	met: number; // required must-haves already on the CV
	have: number; // answered "have" — has it, just not written down
	lack: number; // answered "lack" — a genuine gap
	unanswered: number; // gaps not yet sorted through the questions
	covered: number; // met + have
	verdict: FitVerdict;
}

export function resolveFit(assessment: FitAssessment, answers: FitAnswer[] | undefined): ResolvedFit {
	const { met, total } = requiredStats(assessment);
	const gaps = Math.max(0, total - met);

	// Clamp so answers left over from an earlier, different assessment cannot exceed the real gaps.
	const rawHave = (answers ?? []).filter((a) => a.answer === "have").length;
	const rawLack = (answers ?? []).filter((a) => a.answer === "lack").length;
	const have = Math.min(rawHave, gaps);
	const lack = Math.min(rawLack, gaps - have);
	const unanswered = gaps - have - lack;
	const covered = met + have;

	let verdict: FitVerdict;
	if (total === 0) {
		verdict = "strong";
	} else if (unanswered > 0) {
		verdict = "review";
	} else {
		const ratio = covered / total;
		verdict = ratio >= STRONG_THRESHOLD ? "strong" : ratio >= PARTIAL_THRESHOLD ? "partial" : "weak";
	}

	return { total, met, have, lack, unanswered, covered, verdict };
}
